import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';
import { GoatHealthLog, TreatmentType, VaccineType } from '../../types/api';

const GoatHealthLogsPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [logs, setLogs] = useState<GoatHealthLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterType, setFilterType] = useState<TreatmentType | ''>('');
  const [filterVaccineType, setFilterVaccineType] = useState<VaccineType | ''>('');

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadLogs();
    }
  }, [selectedLocation?.id, businessType, filterTag, filterType, filterVaccineType]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ location_id: selectedLocation?.id || '' });
      if (filterTag) params.append('tag', filterTag);
      if (filterType) params.append('treatment_type', filterType);
      if (filterVaccineType) params.append('vaccine_type', filterVaccineType);
      
      const response = await apiClient.get(`/goat/health-logs?${params.toString()}`);
      setLogs(response.data || []);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load health logs'));
    } finally {
      setLoading(false);
    }
  };

  const getTreatmentTypeColor = (type: TreatmentType) => {
    switch (type) {
      case 'vaccination': return 'bg-blue-100 text-blue-800';
      case 'deworming': return 'bg-green-100 text-green-800';
      case 'treatment': return 'bg-red-100 text-red-800';
      case 'other': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1 min-w-0">
            <Link to="/goat/logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600 flex-shrink-0">
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">💊 Health Logs</h1>
              <p className="text-sm sm:text-base text-gray-600 truncate">{selectedLocation.name}</p>
            </div>
          </div>
        </div>
        {/* Action Buttons - Stack on mobile, horizontal on larger screens */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
          <Link
            to="/goat/health-logs/recommendations"
            className="btn-touch bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 text-sm sm:text-base"
          >
            <span>View Recommendations</span>
          </Link>
          <div className="flex gap-2">
            <Link
              to="/goat/health-logs/vaccination/new"
              className="btn-touch bg-purple-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center justify-center space-x-1 sm:space-x-2 flex-1 sm:flex-initial text-sm sm:text-base"
            >
              <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Add Vaccination</span>
              <span className="sm:hidden">Add</span>
            </Link>
            <Link
              to="/goat/health/new"
              className="btn-touch bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-1 sm:space-x-2 flex-1 sm:flex-initial text-sm sm:text-base"
            >
              <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Record Treatment</span>
              <span className="sm:hidden">Treatment</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="form-label text-sm sm:text-base">Filter by Goat Tag</label>
            <input
              type="text"
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              className="form-input text-sm sm:text-base"
              placeholder="Enter tag number"
            />
          </div>
          <div>
            <label className="form-label text-sm sm:text-base">Filter by Treatment Type</label>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value as TreatmentType | '');
                if (e.target.value !== 'vaccination') {
                  setFilterVaccineType('');
                }
              }}
              className="form-input text-sm sm:text-base"
            >
              <option value="">All Types</option>
              <option value="vaccination">Vaccination</option>
              <option value="deworming">Deworming</option>
              <option value="treatment">Treatment</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="form-label text-sm sm:text-base">Filter by Vaccine Type</label>
            <select
              value={filterVaccineType}
              onChange={(e) => setFilterVaccineType(e.target.value as VaccineType | '')}
              className="form-input text-sm sm:text-base"
              disabled={filterType !== 'vaccination' && filterType !== ''}
            >
              <option value="">All Vaccines</option>
              <option value="PPR">PPR</option>
              <option value="ET_TT">ET + TT</option>
              <option value="FMD_HS_BQ">FMD + HS + BQ</option>
              <option value="GOAT_POX">Goat Pox</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Health Records ({logs.length})
        </h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : logs.length > 0 ? (
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="font-semibold text-gray-900 text-sm sm:text-base">Tag: {log.tag}</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getTreatmentTypeColor(log.treatment_type)}`}>
                        {log.treatment_type}
                      </span>
                      {log.vaccine_type && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 whitespace-nowrap">
                          {log.vaccine_type}
                        </span>
                      )}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                      <div>Date: {new Date(log.health_date).toLocaleDateString()}</div>
                      {log.medicine && <div>Medicine: {log.medicine}</div>}
                      {log.dose && <div>Dose: {log.dose}</div>}
                      {log.notes && <div className="break-words">Notes: {log.notes}</div>}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 flex-shrink-0">
                    {new Date(log.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">💊</div>
            <p className="text-gray-500 mb-4">No health records found</p>
            <Link
              to="/goat/health/new"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Record First Treatment
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoatHealthLogsPage;
